import type { FC, ReactNode } from 'react'

import {
    ArrowRight,
    Check,
    Clock,
    Heart,
    Plus
} from 'lucide-react'
import { Layout } from '@/components/generals/layout'
import { Seo } from '@/components/generals/seo'
import { PageHero } from '@/components/generals/page-hero'
import { ButtonLink } from '@/components/generals/button'
import { SectionHeading } from '@/components/generals/section-heading'
import { ProcessSection } from '@/components/generals/process-section'
import { ConsultationBand } from '@/components/generals/consultation-band'
import { FAQS, INCLUSIONS, SERVICES } from '@/constants/services'

const ServicesPage: FC = (): ReactNode => {
    return (
        <Layout>
            <Seo
                title="Services"
                description="Full planning and design, partial planning, wedding week management and destination weddings, for Indian weddings in India and abroad."
            />

            <PageHero
                eyebrow="Services"
                title={<>Planning, <em className='text-primary'>your way</em></>}
                description="Whether you want us from the first venue visit or only for the wedding week, there is a way of working that fits your family."
            >
                <nav aria-label="Services on this page" className='mt-10'>
                    <ul className='flex flex-wrap gap-3'>
                        {SERVICES.map(service => (
                            <li key={service.id}>
                                <a
                                    href={`#${service.id}`}
                                    className='inline-flex rounded-full border border-border bg-card/80 px-5 py-2.5 text-sm font-medium text-foreground/85 transition-colors hover:border-primary/40 hover:text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring'
                                >
                                    {service.name}
                                </a>
                            </li>
                        ))}
                    </ul>
                </nav>
            </PageHero>

            <section aria-label="Our services" className='py-20 lg:py-28'>
                <div className='mx-auto max-w-7xl space-y-8 px-5 sm:px-8'>
                    {SERVICES.map((service, index) => (
                        <article
                            key={service.id}
                            id={service.id}
                            aria-labelledby={`${service.id}-title`}
                            className='grid scroll-mt-28 gap-10 rounded-[2rem] border border-border bg-card p-7 shadow-[0_30px_60px_-50px_rgba(74,13,24,0.5)] sm:p-10 lg:grid-cols-[1.1fr_0.9fr] lg:gap-16 lg:p-14'
                        >
                            <div>
                                <span aria-hidden='true' className='font-heading text-6xl italic leading-none text-gold/50'>
                                    {String(index + 1).padStart(2, '0')}
                                </span>

                                <h2 id={`${service.id}-title`} className='mt-6 text-4xl leading-tight text-foreground sm:text-5xl'>
                                    {service.name}
                                </h2>

                                <p className='mt-4 font-heading text-2xl italic leading-snug text-primary'>
                                    {service.tagline}
                                </p>

                                <p className='mt-6 text-lg leading-relaxed text-muted-foreground'>
                                    {service.description}
                                </p>

                                <dl className='mt-8 grid gap-4 sm:grid-cols-2'>
                                    <div className='rounded-2xl bg-secondary/80 p-5'>
                                        <dt className='flex items-center gap-2 text-xs font-medium uppercase tracking-[0.2em] text-gold'>
                                            <Heart aria-hidden='true' className='size-4' />
                                            Best for
                                        </dt>

                                        <dd className='mt-2 leading-relaxed text-foreground/85'>
                                            {service.bestFor}
                                        </dd>
                                    </div>

                                    <div className='rounded-2xl bg-secondary/80 p-5'>
                                        <dt className='flex items-center gap-2 text-xs font-medium uppercase tracking-[0.2em] text-gold'>
                                            <Clock aria-hidden='true' className='size-4' />
                                            Timing
                                        </dt>

                                        <dd className='mt-2 leading-relaxed text-foreground/85'>
                                            {service.leadTime}
                                        </dd>
                                    </div>
                                </dl>

                                <ButtonLink href={`/contact?service=${service.id}`} className='mt-8'>
                                    Ask about {service.name.toLowerCase()}
                                    <ArrowRight aria-hidden='true' />
                                </ButtonLink>
                            </div>

                            <div className='rounded-3xl border border-gold/25 bg-background p-7 sm:p-8'>
                                <h3 className='text-2xl text-foreground'>
                                    What's included
                                </h3>

                                <ul className='mt-6 space-y-4'>
                                    {service.includes.map(item => (
                                        <li key={item} className='flex gap-3'>
                                            <span aria-hidden='true' className='mt-1 flex size-5 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary'>
                                                <Check className='size-3' />
                                            </span>

                                            <span className='leading-relaxed text-foreground/85'>
                                                {item}
                                            </span>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        </article>
                    ))}
                </div>
            </section>

            <section id='included' aria-labelledby='included-title' className='bg-secondary/70 py-24 lg:py-32'>
                <div className='mx-auto max-w-7xl px-5 sm:px-8'>
                    <SectionHeading
                        id='included-title'
                        eyebrow="Every plan"
                        title={<>Included, <em className='text-primary'>whichever you choose</em></>}
                        description="However you work with us, these come as standard."
                        align='center'
                    />

                    <ul className='mt-14 grid gap-5 sm:grid-cols-2 lg:grid-cols-3'>
                        {INCLUSIONS.map(inclusion => {
                            const Icon = inclusion.icon

                            return (
                                <li key={inclusion.title} className='rounded-3xl border border-border bg-card p-7'>
                                    <span className='flex size-12 items-center justify-center rounded-full bg-accent text-primary'>
                                        <Icon aria-hidden='true' className='size-5' />
                                    </span>

                                    <h3 className='mt-6 text-2xl leading-tight text-foreground'>
                                        {inclusion.title}
                                    </h3>

                                    <p className='mt-3 leading-relaxed text-muted-foreground'>
                                        {inclusion.description}
                                    </p>
                                </li>
                            )
                        })}
                    </ul>
                </div>
            </section>

            <ProcessSection />

            <section id='faq' aria-labelledby='faq-title' className='bg-secondary/70 py-24 lg:py-32'>
                <div className='mx-auto grid max-w-7xl gap-12 px-5 sm:px-8 lg:grid-cols-[0.8fr_1.2fr] lg:gap-20'>
                    <div>
                        <SectionHeading
                            id='faq-title'
                            eyebrow="Questions"
                            title={<>Things couples <em className='text-primary'>often ask</em></>}
                            description="Something we have not covered? Ask us anything in your inquiry and we will answer it on the first call."
                        />

                        <ButtonLink href='/contact' variant='outline' className='mt-8'>
                            Ask us a question
                        </ButtonLink>
                    </div>

                    <div className='divide-y divide-border rounded-3xl border border-border bg-card px-6 sm:px-8'>
                        {FAQS.map(faq => (
                            <details key={faq.question} className='group py-6'>
                                <summary className='flex cursor-pointer list-none items-start justify-between gap-6 rounded-md text-left font-heading text-2xl leading-snug text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring [&::-webkit-details-marker]:hidden'>
                                    {faq.question}

                                    <span aria-hidden='true' className='mt-1 flex size-8 shrink-0 items-center justify-center rounded-full border border-border text-primary transition-transform duration-300 group-open:rotate-45 motion-reduce:transition-none'>
                                        <Plus className='size-4' />
                                    </span>
                                </summary>

                                <p className='mt-4 pr-2 leading-relaxed text-muted-foreground sm:pr-14'>
                                    {faq.answer}
                                </p>
                            </details>
                        ))}
                    </div>
                </div>
            </section>

            <ConsultationBand />
        </Layout>
    )
}

export default ServicesPage
